import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../../styles/interviews.css';
import APIService from '../../services/APIService';
import { IconBrain, IconTarget, IconBars, IconPerson, IconSpark, IconClock, IconSearch } from './icons';

/*
 * InterviewsPage — "Find Your Next Interview": the public discovery surface
 * for creator interviews. Browse is free; attempting goes through the
 * entitlement flow (beta subscriptions are free, one click).
 *
 * Sections are honest to real data: Recommended = matches your session scope,
 * "From creators you're subscribed to" = your actual entitlements, and
 * New & Recently Added = newest first (the API's default order).
 */

const COVERS = [
  { test: /rag|retrieval|vector|search/i, Icon: IconTarget, hue: '265deg' },
  { test: /llm|nlp|transformer|bert/i, Icon: IconSpark, hue: '210deg' },
  { test: /production|mlops|deploy|system/i, Icon: IconBars, hue: '160deg' },
  { test: /behavior|story|star/i, Icon: IconPerson, hue: '330deg' },
];
const coverFor = (ivw) => {
  const hay = `${ivw.topic} ${ivw.title} ${ivw.role}`;
  return COVERS.find((c) => c.test.test(hay)) || { Icon: IconBrain, hue: '245deg' };
};

const Card = ({ ivw, onOpen, wide = false }) => {
  const { Icon, hue } = coverFor(ivw);
  return (
    <button type="button" className={`iv-card${wide ? ' wide' : ''}`} onClick={() => onOpen(ivw)}>
      <div className="iv-cover" style={{ '--hue': hue }}><Icon size={30} /></div>
      <div className="iv-card-body">
        <div className="iv-card-title">{ivw.title}</div>
        <div className="iv-card-by">by {ivw.creator_name || 'DataVerse'} <span className="iv-verified">✓</span></div>
        <div className="iv-card-chips">
          {ivw.topic && <span className="iv-chip t0">{ivw.topic}</span>}
          {ivw.role && <span className="iv-chip t1">{ivw.role}</span>}
        </div>
        <div className="iv-card-meta">
          {ivw.seniority && <span>{ivw.seniority}</span>}
          <span className="iv-meta-time"><IconClock size={13} /> {ivw.duration_min} min</span>
        </div>
      </div>
    </button>
  );
};

const Section = ({ title, icon, items, onOpen, empty }) => (
  (items.length || empty) ? (
    <section className="iv-section">
      <div className="iv-section-head"><span className="iv-section-icon">{icon}</span><h2>{title}</h2></div>
      {items.length
        ? <div className="iv-row">{items.map((i) => <Card key={i.id} ivw={i} onOpen={onOpen} />)}</div>
        : <div className="iv-empty">{empty}</div>}
    </section>
  ) : null
);

const InterviewsPage = ({ config, onLaunch }) => {
  const [all, setAll] = useState(null);          // null = loading
  const [err, setErr] = useState('');
  const [subs, setSubs] = useState({});
  const [query, setQuery] = useState('');
  const [chip, setChip] = useState('All');
  const [open, setOpen] = useState(null);        // the detail overlay's interview
  const [starting, setStarting] = useState(false);
  const [startErr, setStartErr] = useState('');

  useEffect(() => {
    APIService.discoverInterviews()
      .then((d) => setAll(d.interviews || []))
      .catch((e) => { setErr(e.message); setAll([]); });
    APIService.getEntitlements().then((e) => setSubs(e.subs || {})).catch(() => {});
  }, []);

  const chips = useMemo(() => {
    const seen = new Map();
    (all || []).forEach((i) => {
      [i.topic, i.role].filter(Boolean).forEach((t) => seen.set(t, (seen.get(t) || 0) + 1));
    });
    return ['All', ...[...seen.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6).map(([t]) => t)];
  }, [all]);

  const filtered = useMemo(() => {
    let list = all || [];
    if (chip !== 'All') list = list.filter((i) => i.topic === chip || i.role === chip);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((i) => `${i.title} ${i.creator_name} ${i.topic} ${i.role} ${i.seniority}`
        .toLowerCase().includes(q));
    }
    return list;
  }, [all, chip, query]);

  // Recommended = interviews whose topic/role overlaps the session's subjects.
  const scopeNames = useMemo(() => (config?.subjects || []).map((s) => String(s.name || '').toLowerCase()), [config]);
  const recommended = useMemo(() => {
    const match = filtered.filter((i) => scopeNames.some((n) => n
      && (`${i.topic} ${i.title}`.toLowerCase().includes(n.split(' ')[0]))));
    return (match.length ? match : filtered).slice(0, 4);
  }, [filtered, scopeNames]);

  const subscribedTo = useMemo(() => filtered.filter((i) => {
    const s = subs[`creator:${i.creator_id}`];
    return s && s.status === 'active';
  }), [filtered, subs]);

  const start = useCallback(async (ivw) => {
    setStarting(true); setStartErr('');
    try {
      const product = `creator:${ivw.creator_id}`;
      if (!(subs[product] && subs[product].status === 'active')) {
        const res = await APIService.subscribeCreator(ivw.creator_id);
        setSubs(res.entitlements?.subs || { ...subs, [product]: { status: 'active' } });
      }
      const sess = await APIService.startCreatorInterview(ivw.id);
      onLaunch(sess);
    } catch (e) {
      setStartErr(e.message || 'Could not start the interview.');
    } finally { setStarting(false); }
  }, [subs, onLaunch]);

  return (
    <div className="iv-wrap">
      <header className="iv-head">
        <h1>Find Your Next Interview</h1>
        <p>Curated, rubric-driven interviews to help you prepare, practice and ace your dream role.</p>
        <div className="iv-search">
          <IconSearch size={16} />
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search interviews, skills, roles, topics…" />
        </div>
        <div className="iv-chips-row">
          {chips.map((c) => (
            <button key={c} type="button" className={`iv-rolechip${chip === c ? ' on' : ''}`}
              onClick={() => setChip(c)}>{c}</button>
          ))}
        </div>
      </header>

      {all === null ? (
        <div className="iv-loading"><div className="iv-spinner" /> Loading the interview library…</div>
      ) : err ? (
        <div className="iv-empty">{err}</div>
      ) : !filtered.length ? (
        <div className="iv-empty">No interviews match — try a different search or chip.</div>
      ) : (
        <>
          <Section title="Recommended for you" icon="✦" items={recommended} onOpen={setOpen} />
          <Section title="From creators you're subscribed to" icon="♥" items={subscribedTo} onOpen={setOpen} />
          <Section title="New & recently added" icon="✨" items={filtered} onOpen={setOpen} />
        </>
      )}

      {open && (
        <div className="iv-veil" role="dialog" aria-modal="true" onClick={() => !starting && setOpen(null)}>
          <div className="iv-detail" onClick={(e) => e.stopPropagation()}>
            <div className="iv-detail-cover" style={{ '--hue': coverFor(open).hue }}>
              {React.createElement(coverFor(open).Icon, { size: 38 })}
            </div>
            <h2>{open.title}</h2>
            <div className="iv-card-by">by {open.creator_name || 'DataVerse'} <span className="iv-verified">✓</span>
              {open.creator_headline ? ` — ${open.creator_headline}` : ''}</div>
            <div className="iv-detail-grid">
              <div><b>{open.question_count}</b><span>questions</span></div>
              <div><b>{open.duration_min}m</b><span>duration</span></div>
              <div><b>{open.total_marks}</b><span>marks</span></div>
              <div><b>{open.pressure}/5</b><span>pressure</span></div>
            </div>
            <div className="iv-card-chips">
              {open.topic && <span className="iv-chip t0">{open.topic}</span>}
              {open.role && <span className="iv-chip t1">{open.role}</span>}
              {open.seniority && <span className="iv-chip t2">{open.seniority}</span>}
            </div>
            <p className="iv-detail-note">
              A live spoken interview with a rubric-driven interviewer — you'll get a scored,
              per-question report at the end. Included free during beta.
            </p>
            {startErr && <div className="iv-empty">{startErr}</div>}
            <div className="iv-detail-actions">
              <button type="button" className="iv-ghost" onClick={() => setOpen(null)} disabled={starting}>Close</button>
              <button type="button" className="iv-primary" onClick={() => start(open)} disabled={starting}>
                {starting ? 'Preparing your room…' : '▶ Start interview'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewsPage;
